import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Globe, Loader2, RefreshCw } from 'lucide-react';
import {
  formatSiteActivityWhen,
  loadSubscriberSiteActivity,
  siteActivityEventLabel,
  type SiteActivityRow,
} from '../../lib/posthogActivity';

function activitySummary(r: SiteActivityRow): string {
  if (r.path) return r.path;
  if (r.detail) return r.detail;
  return '—';
}

export default function SubscriberSiteActivitySection({
  subscriberId,
}: {
  subscriberId: string;
}) {
  const [rows, setRows] = useState<SiteActivityRow[]>([]);
  const [posthogUrl, setPosthogUrl] = useState<string | null>(null);
  const [lookbackDays, setLookbackDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await loadSubscriberSiteActivity(subscriberId);
      setRows(data.events);
      setPosthogUrl(data.posthogPersonUrl);
      setLookbackDays(data.lookbackDays);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load analytics');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [subscriberId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
            Analytics
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            PostHog — stephansmith.me + BookTime (last {lookbackDays} days, CRM contact id).
          </p>
        </div>
        <div className="flex items-center gap-2">
          {posthogUrl ? (
            <a
              href={posthogUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open in PostHog
            </a>
          ) : null}
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            aria-label="Refresh analytics"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="p-10 flex justify-center">
          <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="p-6 text-sm text-red-700 dark:text-red-300">{error}</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-sm text-gray-500 dark:text-gray-400 text-center">
          No site events for this contact yet. Activity appears after they browse stephansmith.me
          while signed in, use a newsletter login link, or complete a BookTime booking.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr>
              <th className="text-left px-4 py-2 font-medium">When</th>
              <th className="text-left px-4 py-2 font-medium">Event</th>
              <th className="text-left px-4 py-2 font-medium">App</th>
              <th className="text-left px-4 py-2 font-medium">Page / detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {rows.map((r, i) => (
              <tr key={`${r.timestamp}-${r.event}-${i}`} className="text-gray-800 dark:text-gray-200">
                <td className="px-4 py-2 whitespace-nowrap text-gray-600 dark:text-gray-300">
                  {formatSiteActivityWhen(r.timestamp)}
                </td>
                <td className="px-4 py-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200">
                    <Globe className="w-3 h-3" />
                    {siteActivityEventLabel(r.event)}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-300">
                  {r.app ?? '—'}
                </td>
                <td
                  className="px-4 py-2 max-w-[320px] truncate text-xs text-gray-600 dark:text-gray-300"
                  title={activitySummary(r)}
                >
                  {activitySummary(r)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
